import { AxiosError } from 'axios';
import clsx from 'clsx';
import { jwtDecode } from 'jwt-decode';
import { Controller, useForm } from 'react-hook-form';

import { getNowInSeconds, useSetCredentials } from '@quick/auth';
import { BackButton } from '@quick/components/BackButton';
import { Button } from '@quick/components/Button';
import { WrappedInput } from '@quick/components/Forms';
import { useToast } from '@quick/components/Toaster';
import type { LoginRes } from '@quick/go-api-types/users/v2/login';

import { useAppTranslation } from '@/app/i18n/client';
import { standardError } from '@/layers/core/deal/mappers/standardError';
import useRequestConfigHeaders from '@/layers/core/deal/services/useRequestConfig';
import { nonNumbers } from '@/layers/core/helpers/regex';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { InlineSpinner } from './InlineSpinner';

type TF = Omit<LoginRes, 'two_factor'>;
interface TwoFactorValidationProps {
  token: TF;
  goBack: () => void;
  onSuccess?: () => void;
  className?: string;
}

interface FormValues extends TF {
  code: [string, string, string, string, string, string];
}

type CodeIndex = 0 | 1 | 2 | 3 | 4 | 5;

export function TwoFactorValidation({
  token,
  goBack,
  onSuccess,
  className,
}: TwoFactorValidationProps) {
  const { t } = useAppTranslation(['common', 'dl']);
  const { toast } = useToast();
  const setCredentials = useSetCredentials();
  const headers = useRequestConfigHeaders();
  const {
    control,
    handleSubmit,
    watch,
    setFocus,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      access_token: token.access_token,
      refresh_token: token.refresh_token,
      code: Array.from({ length: 6 }, () => '') as FormValues['code'],
    },
  });

  const code = watch('code');

  const isCodeValid =
    code.filter((c) => c.length === 1 && c.trim()).length === 6;

  const verifyCode = handleSubmit(async (data) => {
    try {
      await axios_demand.post(
        '/users/two_factor',
        { ...data, code: data.code.join('') },
        {
          headers,
        }
      );
      setCredentials({
        access: token.access_token,
        refresh: token.refresh_token,
      });
      const decoded = jwtDecode<{
        iat: number;
      }>(token.access_token);
      const userLoginTimeStamp = decoded?.iat;
      if (!userLoginTimeStamp) return;

      const differenceBetweenLoginAndNow =
        getNowInSeconds() - userLoginTimeStamp;
      const minutes = Math.abs(differenceBetweenLoginAndNow / 60);

      if (minutes >= 1) {
        toast({
          title: t('dl:timeOutOfSync.title'),
          variant: 'warning',
          description: t('dl:timeOutOfSync.description'),
          duration: 20000,
        });
      }
      onSuccess?.();
    } catch (err) {
      err instanceof AxiosError
        ? toast({
            title: t('common:msg.has_ocurred', {
              msg: '',
            }),
            description: standardError(err.response?.data),
            variant: 'danger',
            duration: 3500,
          })
        : toast({
            title: t('common:msg.unexpected_error'),
            variant: 'danger',
            duration: 3500,
          });
    }
  });

  return (
    <div className={clsx('flex flex-col items-center gap-y-6 p-4', className)}>
      <div className="self-start">
        <BackButton text={t('common:goBack')} onClick={goBack} />
      </div>
      <h1 className="text-3xl dark:text-white">
        {t('common:login.2fa.title')}
      </h1>
      <div className="">
        <p className="dark:text-gray-light max-w-lg text-center text-gray-700">
          {t('common:login.2fa.description')}
        </p>
      </div>
      <form onSubmit={verifyCode} className="flex flex-col gap-4">
        <fieldset className="flex flex-row gap-2">
          {code.map((_, index) => (
            <Controller
              key={index}
              name={`code.${index as CodeIndex}`}
              control={control}
              rules={{ required: t('common:msg.required_field') }}
              render={({ field }) => (
                <WrappedInput
                  {...field}
                  className="h-10 w-10"
                  innerClassName="text-center no-arrows"
                  aria-label={`${t('common:code')} ${index + 1}`}
                  autoFocus={index === 0}
                  id={`verification-code-${index}`}
                  type="tel"
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace') {
                      if (field.value || index === 0) return;
                      setFocus(`code.${(index - 1) as 1}`, {
                        shouldSelect: true,
                      });
                    }
                  }}
                  inputMode="numeric"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.replaceAll(nonNumbers, '');
                    let codeValues = code;
                    if (value.length < 2) {
                      field.onChange(value);
                    } else {
                      const valueArray = value.slice(0, 6 - index).split('');
                      codeValues = replaceArrayValues(
                        code,
                        index,
                        valueArray
                      ) as FormValues['code'];
                      setValue('code', codeValues);
                    }
                    // can access new code value here
                    const codeIndex = codeValues.findIndex((c) => !c.trim());
                    if (!value.trim()) return;
                    if (codeIndex === -1) setFocus('code.5');
                    else setFocus(`code.${codeIndex as CodeIndex}`);
                    // verify code automatically if all fields are filled
                    const isValid =
                      codeValues.filter((c) => c.length === 1 && c.trim())
                        .length === 6;
                    if (isValid) {
                      verifyCode();
                    }
                  }}
                />
              )}
            />
          ))}
        </fieldset>
        <Button
          className="flex items-center justify-center gap-2"
          disabled={!isDirty || isSubmitting || !isCodeValid}
          type="submit"
        >
          {t('common:verify')} {isSubmitting && <InlineSpinner />}
        </Button>
      </form>
    </div>
  );
}

function replaceArrayValues<T>(array: T[], index: number, value: T[]) {
  return [
    ...array.slice(0, index),
    ...value,
    ...array.slice(index + value.length),
  ];
}
